import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import { motion } from 'framer-motion';
import { searchProducts, filterProductsByCategory, Product } from '../services/productService';

// Extended product type to include UI-specific properties
interface ExtendedProduct extends Product {
  isNew: boolean;
  sales: number;
  creator: string;
}

interface SearchFilters {
  category: string;
  priceRange: [number, number];
  sortBy: string;
}

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const searchQuery = queryParams.get('q') || '';

  const [searchResults, setSearchResults] = useState<ExtendedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<SearchFilters>({
    category: '',
    priceRange: [0, 500],
    sortBy: 'relevance'
  });

  // Available categories - these should ideally come from an API call
  const categories = ['UI Kit', 'Template', 'API', 'Plugin', 'Component', 'Framework', 'Icon Set'];

  // Fetch search results
  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);

      try {
        let results: Product[];

        // If a category filter is applied, use the category filter API
        if (filters.category) {
          results = await filterProductsByCategory(filters.category);

          // If there's also a search term, filter the category results
          if (searchQuery) {
            const searchTerms = searchQuery.toLowerCase().split(' ');
            results = results.filter(product =>
              searchTerms.some(term =>
                product.title.toLowerCase().includes(term) ||
                product.description.toLowerCase().includes(term) ||
                product.tags.some(tag => tag.toLowerCase().includes(term))
              )
            );
          }
        } else if (searchQuery) {
          // If there's only a search term without category, use the search API
          results = await searchProducts(searchQuery);
        } else {
          // If neither search nor category, load all products
          results = await searchProducts('');
        }

        // Transform API products to include UI-specific fields
        const extendedResults: ExtendedProduct[] = results.map(product => ({
          ...product,
          isNew: new Date(product.releaseDate) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          sales: Math.floor(product.downloads * 0.8), // Estimate sales from downloads
          creator: product.sellerName
        }));

        // Apply price filter
        let filteredResults = extendedResults.filter(product =>
          product.price >= filters.priceRange[0] &&
          product.price <= filters.priceRange[1]
        );

        // Apply sorting
        switch (filters.sortBy) {
          case 'price-low':
            filteredResults.sort((a, b) => a.price - b.price);
            break;
          case 'price-high':
            filteredResults.sort((a, b) => b.price - a.price);
            break;
          case 'newest':
            filteredResults.sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime());
            break;
          case 'rating':
            filteredResults.sort((a, b) => b.rating - a.rating);
            break;
          case 'popularity':
            filteredResults.sort((a, b) => b.downloads - a.downloads);
            break;
          // 'relevance' is default and handled by the API
        }

        setSearchResults(filteredResults);
      } catch (error) {
        console.error('Error fetching search results:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [searchQuery, filters]);

  // Handle search input
  const [searchInput, setSearchInput] = useState(searchQuery);

  // Perform search when form is submitted
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchInput)}`);
  };

  // Filter states
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 500]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('relevance');
  const [ratingFilter, setRatingFilter] = useState<number>(0);

  const handleCategoryChange = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value);
  };

  const handlePriceChange = (index: number, value: number) => {
    const newPriceRange = [...priceRange] as [number, number];
    newPriceRange[index] = value;
    setPriceRange(newPriceRange);
  };

  const handleRatingChange = (rating: number) => {
    setRatingFilter(rating === ratingFilter ? 0 : rating);
  };

  const filteredResults = searchResults
    .filter(product =>
      product.price >= priceRange[0] &&
      product.price <= priceRange[1] &&
      (selectedCategories.length === 0 || selectedCategories.includes(product.category)) &&
      (ratingFilter === 0 || product.rating >= ratingFilter)
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'rating':
          return b.rating - a.rating;
        case 'newest':
          return a.isNew ? -1 : b.isNew ? 1 : 0;
        case 'best-selling':
          return b.sales - a.sales;
        default: // relevance (default sort)
          return 0;
      }
    });

  const resetFilters = () => {
    setPriceRange([0, 500]);
    setSelectedCategories([]);
    setSortBy('relevance');
    setRatingFilter(0);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">
          {searchQuery ? `Search results for "${searchQuery}"` : 'Browse all assets'}
        </h1>
        <p className="text-[var(--color-muted)]">
          {filteredResults.length} {filteredResults.length === 1 ? 'result' : 'results'} found
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Filters Sidebar */}
        <div className="lg:w-1/4">
          <div className="bg-[#1F1A27] rounded-lg p-6 sticky top-24">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-medium">Filters</h2>
              {(selectedCategories.length > 0 || ratingFilter > 0 || priceRange[0] > 0 || priceRange[1] < 500) && (
                <button
                  className="text-sm text-[#7F56D9] hover:underline"
                  onClick={resetFilters}
                >
                  Reset All
                </button>
              )}
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <h3 className="font-medium mb-3">Price Range</h3>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-[var(--color-muted)]">${priceRange[0]}</span>
                <span className="text-sm text-[var(--color-muted)]">${priceRange[1]}</span>
              </div>
              <div className="flex gap-4">
                <input
                  type="range"
                  min="0"
                  max="500"
                  step="10"
                  value={priceRange[0]}
                  onChange={e => handlePriceChange(0, parseInt(e.target.value))}
                  className="w-full"
                />
                <input
                  type="range"
                  min="0"
                  max="500"
                  step="10"
                  value={priceRange[1]}
                  onChange={e => handlePriceChange(1, parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
              <div className="flex gap-2 mt-3">
                <input
                  type="number"
                  min="0"
                  max={priceRange[1]}
                  value={priceRange[0]}
                  onChange={e => handlePriceChange(0, parseInt(e.target.value))}
                  className="w-1/2 bg-[#2A2633] border border-white/10 rounded px-2 py-1 text-sm"
                />
                <input
                  type="number"
                  min={priceRange[0]}
                  max="500"
                  value={priceRange[1]}
                  onChange={e => handlePriceChange(1, parseInt(e.target.value))}
                  className="w-1/2 bg-[#2A2633] border border-white/10 rounded px-2 py-1 text-sm"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <h3 className="font-medium mb-3">Categories</h3>
              <div className="space-y-2">
                {categories.map(category => (
                  <div key={category} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`category-${category}`}
                      checked={selectedCategories.includes(category)}
                      onChange={() => handleCategoryChange(category)}
                      className="mr-2"
                    />
                    <label htmlFor={`category-${category}`} className="text-sm cursor-pointer">
                      {category}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Rating */}
            <div className="mb-6">
              <h3 className="font-medium mb-3">Rating</h3>
              <div className="space-y-2">
                {[4, 3, 2, 1].map(rating => (
                  <button
                    key={rating}
                    className={`flex items-center w-full py-1 px-2 rounded hover:bg-[#2A2633] transition-colors ${
                      ratingFilter === rating ? 'bg-[#2A2633]' : ''
                    }`}
                    onClick={() => handleRatingChange(rating)}
                  >
                    <div className="flex text-yellow-400">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <svg
                          key={i}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill={i < rating ? 'currentColor' : 'none'}
                          stroke={i < rating ? 'none' : 'currentColor'}
                          className={i < rating ? '' : 'text-gray-500'}
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" strokeWidth="1.5" />
                        </svg>
                      ))}
                    </div>
                    <span className="ml-2 text-sm">& Up</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:w-3/4">
          <div className="bg-[#1F1A27] rounded-lg p-4 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-[var(--color-muted)]">
              Showing {filteredResults.length} of {searchResults.length} assets
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="sort" className="text-sm">Sort by:</label>
              <select
                id="sort"
                className="bg-[#2A2633] border border-white/10 rounded-md py-1 px-2 text-sm"
                value={sortBy}
                onChange={handleSortChange}
              >
                <option value="relevance">Relevance</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Rating</option>
                <option value="newest">Newest</option>
                <option value="best-selling">Best Selling</option>
              </select>
            </div>
          </div>

          {loading ? (
            // Loading Skeletons
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="rounded-lg bg-[#1F1A27] overflow-hidden animate-pulse">
                  <div className="h-48 bg-[#2A2633]"></div>
                  <div className="p-4">
                    <div className="h-6 bg-[#2A2633] rounded mb-3 w-3/4"></div>
                    <div className="h-4 bg-[#2A2633] rounded mb-2 w-full"></div>
                    <div className="h-4 bg-[#2A2633] rounded mb-2 w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredResults.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResults.map(product => (
                <Card
                  key={product.id}
                  id={product.id}
                  title={product.title}
                  creator={product.creator}
                  price={product.price}
                  isLiked={product.isLiked}
                  imageUrl={product.imageUrl}
                  rating={product.rating}
                  sales={product.sales}
                  badges={product.badges}
                  isNew={product.isNew}
                  discount={product.discount}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-[#2A2633] flex items-center justify-center mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" className="text-[#7F56D9]">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" strokeWidth="1.5" />
                </svg>
              </div>
              <h2 className="text-xl font-medium mb-2">No results found</h2>
              <p className="text-[var(--color-muted)] mb-6 max-w-md">
                We couldn't find any assets matching your search criteria. Try adjusting your filters or try a different search term.
              </p>
              <button
                onClick={resetFilters}
                className="btn-primary py-2 px-4"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
